import { CalendarDays } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import { sanitizeHref, sanitizeUserInput } from "../../utils/sanitizeInput";

interface FieldProps {
  value?: string | number;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?:
    | (() => void)
    | ((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void);
  name?: string;
  ref?: React.Ref<HTMLInputElement | HTMLTextAreaElement | null>;
  [key: string]: unknown; // Allow additional props from React Hook Form
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

interface ErrorProps {
  message?: string;
}

type InputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "date"
  | "textarea";

interface SuffixDropdownOption {
  value: string;
  label: string;
}

interface InputFormProps {
  label?: string;
  id?: string;
  errors?: ErrorProps;
  field?: FieldProps;
  inputType?: InputType;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
  required?: boolean;
  optional?: boolean;
  maxLength?: number;
  minLength?: number;
  min?: number;
  showCharCount?: boolean;
  hint?: string;
  helpButton?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode; // For custom suffix elements
  suffixOptions?: SuffixDropdownOption[]; // For dropdown suffix (alternative to suffix prop)
  suffixValue?: string; // Current selected value for suffix dropdown
  onSuffixChange?: (value: string) => void; // Callback when suffix dropdown value changes
  /** Sets `data-field` on the input/textarea for validation focus targeting. */
  dataField?: string;
}

export function RequiredIndicator() {
  return (
    <span className="text-danger" aria-hidden>
      *
    </span>
  );
}

const InputForm: React.FC<InputFormProps> = ({
  label,
  id,
  errors,
  field,
  inputType = "text",
  isDisabled,
  placeholder,
  className = "",
  required,
  optional,
  maxLength,
  minLength,
  min,
  showCharCount,
  hint,
  helpButton,
  prefix,
  suffix,
  suffixOptions,
  suffixValue,
  onSuffixChange,
  dataField,
}) => {
  const { t, dir } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSuffixDropdownOpen, setIsSuffixDropdownOpen] = useState(false);
  const suffixDropdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Close suffix dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suffixDropdownRef.current &&
        !suffixDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSuffixDropdownOpen(false);
      }
    };

    if (isSuffixDropdownOpen && suffixOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSuffixDropdownOpen, suffixOptions]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  // Auto-resize textarea (only if not using max-height constraint)
  useEffect(() => {
    if (inputType === "textarea" && textareaRef.current) {
      const textarea = textareaRef.current;
      const computedStyle = window.getComputedStyle(textarea);
      const hasMaxHeight = computedStyle.maxHeight !== "none";

      // Only auto-resize if max-height is not set
      if (!hasMaxHeight) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, [field?.value, inputType]);

  const sanitizeTypedValue = (raw: string) =>
    inputType === "url" ? sanitizeHref(raw) : sanitizeUserInput(raw);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const sanitized = sanitizeTypedValue(e.target.value);
    if (sanitized !== e.target.value) {
      e.target.value = sanitized;
    }
    // Preserve cursor position
    const cursorPosition = e.target.selectionStart;

    if (field?.onChange) {
      (
        field.onChange as (
          value: React.ChangeEvent<HTMLTextAreaElement>,
        ) => void
      )(e);
    }

    // Auto-resize and restore cursor position after React updates (only if max-height not set)
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const computedStyle = window.getComputedStyle(textarea);
      const hasMaxHeight = computedStyle.maxHeight !== "none";

      // Only auto-resize if max-height is not set
      if (!hasMaxHeight) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }

      // Restore cursor position in next tick to avoid cursor jump
      setTimeout(() => {
        if (textarea === document.activeElement) {
          textarea.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    }
  };

  const { ref: fieldRef, ...fieldWithoutRef } = field ?? {};

  // For number inputs, wrap onChange to convert string → number so Zod z.number() validates correctly
  const processedField =
    field && inputType === "number"
      ? {
          ...fieldWithoutRef,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            (field.onChange as unknown as (value: string | number) => void)?.(
              raw === "" ? "" : Number(raw),
            );
          },
        }
      : field && inputType !== "password"
        ? {
            ...fieldWithoutRef,
            onChange: (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              const sanitized = sanitizeTypedValue(e.target.value);
              if (sanitized !== e.target.value) {
                e.target.value = sanitized;
              }
              field.onChange?.(e);
            },
          }
        : fieldWithoutRef;

  const setInputRef = (element: HTMLInputElement | null) => {
    assignRef(
      fieldRef as React.Ref<HTMLInputElement | null> | undefined,
      element,
    );
  };

  const setTextareaRefs = (element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    assignRef(
      fieldRef as React.Ref<HTMLTextAreaElement | null> | undefined,
      element,
    );
  };

  const currentValue = field?.value ?? "";
  const currentLength =
    typeof currentValue === "string"
      ? currentValue.length
      : String(currentValue).length;

  const baseInputClassName = `flex w-full items-center rounded-lg border outline-none transition-all duration-200 text-primary-text placeholder-muted-foreground disabled:bg-muted disabled:text-muted-foreground disabled:border-input ${
    errors
      ? "border-danger focus:border-danger focus:ring-2 focus:ring-danger/20"
      : "border-input hover:border-border focus:border-ring focus:ring-2 focus:ring-ring/20"
  }`;

  // Browsers force LTR on tel/email/url; override so placeholders follow the UI language.
  const inheritsUiDirection =
    inputType === "tel" || inputType === "email" || inputType === "url";
  const uiDirectionStyle = inheritsUiDirection
    ? ({ direction: dir, textAlign: "start" } as const)
    : undefined;

  const inputClassName =
    inputType === "textarea"
      ? `${baseInputClassName} text-start px-4 py-3 text-sm bg-secondary resize-none overflow-x-hidden min-h-[4rem]`
      : `${baseInputClassName} text-start px-4 py-3 shadow-sm bg-secondary`;

  return (
    <div className={`flex flex-col w-full gap-1 ${className} `}>
      <div className="flex items-center gap-2">
        <label htmlFor={id} className="text-primary-text text-sm font-medium">
          {label}
          {required && <RequiredIndicator />}
          {optional && (
            <span className="text-muted-foreground text-xs ms-1">
              {t("common.optional")}
            </span>
          )}
        </label>
        {helpButton}
      </div>
      <div className="relative">
        {prefix || suffix || suffixOptions ? (
          <div
            className={`bg-secondary border border-input rounded-lg shadow-sm flex items-center w-full ${suffixOptions ? "overflow-visible" : suffix ? "overflow-visible" : "overflow-hidden"} transition-all duration-200 relative ${
              errors
                ? "border-danger focus-within:border-danger focus-within:ring-2 focus-within:ring-danger/20"
                : "hover:border-border focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20"
            } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {prefix && (
              <div className="flex shrink-0 items-center rounded-s-lg border-e border-input bg-secondary px-3.5 py-2.5">
                <span className="text-base font-normal text-secondary-foreground leading-6">
                  {prefix}
                </span>
              </div>
            )}
            <input
              {...processedField}
              ref={setInputRef}
              id={id}
              data-field={dataField}
              type={
                inputType === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : inputType
              }
              placeholder={placeholder || label}
              dir={inheritsUiDirection ? dir : undefined}
              className={`flex-1 px-3.5 py-2.5 text-base font-normal text-primary-text placeholder-muted-foreground bg-secondary outline-none border-none disabled:bg-muted disabled:text-muted-foreground text-start ${
                prefix ? "" : "rounded-s-lg"
              } ${suffix || suffixOptions ? "" : "rounded-e-lg"}`}
              disabled={isDisabled}
              aria-invalid={errors ? "true" : "false"}
              maxLength={maxLength}
              minLength={minLength}
              min={min}
              style={{
                paddingInlineEnd:
                  inputType === "password" ? "2.5rem" : undefined,
                ...uiDirectionStyle,
              }}
            />
            {suffixOptions ? (
              <div
                ref={suffixDropdownRef}
                className="relative flex h-full shrink-0 items-center border-s border-input"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) {
                      setIsSuffixDropdownOpen(!isSuffixDropdownOpen);
                    }
                  }}
                  disabled={isDisabled}
                  className="flex h-full items-center gap-2 rounded-e-lg bg-secondary px-4 py-2.5 text-base font-normal text-primary-text transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span>
                    {(suffixOptions as SuffixDropdownOption[]).find(
                      (opt) => opt.value === suffixValue,
                    )?.label ||
                      suffixOptions[0]?.label ||
                      ""}
                  </span>
                  <svg
                    className={`w-4 h-4 text-muted-foreground transition-transform ${
                      isSuffixDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isSuffixDropdownOpen && suffixOptions && (
                  <div className="absolute inset-e-0 top-full z-100 mt-1 min-w-30 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
                    {(suffixOptions as SuffixDropdownOption[]).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSuffixChange?.(option.value);
                          setIsSuffixDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-start text-sm hover:bg-accent first:rounded-t-lg last:rounded-b-lg transition-colors ${
                          suffixValue === option.value
                            ? "bg-brand-primary/10 text-brand-primary font-medium"
                            : "text-primary-text"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : suffix ? (
              <div className="flex shrink-0 items-center border-s border-input">
                {suffix}
              </div>
            ) : null}
            {inputType === "password" && (
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-e-3 top-[50%] translate-y-[-50%] text-muted-foreground hover:text-primary-text z-10"
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : inputType === "textarea" ? (
          <textarea
            {...fieldWithoutRef}
            ref={setTextareaRefs}
            id={id}
            data-field={dataField}
            onChange={handleTextareaChange}
            placeholder={placeholder || label}
            className={inputClassName}
            disabled={isDisabled}
            aria-invalid={errors ? "true" : "false"}
            maxLength={maxLength}
            minLength={minLength}
            rows={2}
          />
        ) : suffixOptions ? (
          <div
            className={`bg-secondary border border-input rounded-lg shadow-sm flex items-center w-full overflow-visible transition-all duration-200 relative ${
              errors
                ? "border-danger focus-within:border-danger focus-within:ring-2 focus-within:ring-danger/20"
                : "hover:border-border focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20"
            } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              {...processedField}
              ref={setInputRef}
              id={id}
              data-field={dataField}
              type={
                inputType === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : inputType
              }
              placeholder={placeholder || label}
              dir={inheritsUiDirection ? dir : undefined}
              className="flex-1 rounded-s-lg border-none bg-secondary px-3.5 py-2.5 text-base font-normal text-primary-text outline-none placeholder-muted-foreground disabled:bg-muted disabled:text-muted-foreground text-start"
              disabled={isDisabled}
              style={uiDirectionStyle}
              aria-invalid={errors ? "true" : "false"}
              maxLength={maxLength}
            />
            <div
              ref={suffixDropdownRef}
              className="relative flex h-full shrink-0 items-center border-s border-input"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    setIsSuffixDropdownOpen(!isSuffixDropdownOpen);
                  }
                }}
                disabled={isDisabled}
                className="flex h-full items-center gap-2 rounded-e-lg bg-secondary px-4 py-2.5 text-base font-normal text-primary-text transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span>
                  {suffixOptions &&
                  (suffixOptions as SuffixDropdownOption[]).length > 0
                    ? (suffixOptions as SuffixDropdownOption[]).find(
                        (opt) => opt.value === suffixValue,
                      )?.label ||
                      (suffixOptions as SuffixDropdownOption[])[0]?.label ||
                      ""
                    : ""}
                </span>
                <svg
                  className={`w-4 h-4 text-muted-foreground transition-transform ${
                    isSuffixDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isSuffixDropdownOpen && suffixOptions && (
                <div className="absolute inset-e-0 top-full z-100 mt-1 min-w-30 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
                  {(suffixOptions as SuffixDropdownOption[]).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSuffixChange?.(option.value);
                        setIsSuffixDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-start text-sm hover:bg-accent first:rounded-t-lg last:rounded-b-lg transition-colors ${
                        suffixValue === option.value
                          ? "bg-brand-primary/10 text-brand-primary font-medium"
                          : "text-primary-text"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {inputType === "password" && (
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-e-3 top-[50%] translate-y-[-50%] text-muted-foreground hover:text-primary-text z-10"
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : (
          <>
            {inputType === "date" && (
              <div className="pointer-events-none absolute inset-s-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">
                <CalendarDays className="h-5 w-5" aria-hidden />
              </div>
            )}
            <input
              {...processedField}
              ref={setInputRef}
              id={id}
              type={
                inputType === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : inputType
              }
              placeholder={
                inputType === "date"
                  ? placeholder || "Select"
                  : placeholder || label
              }
              dir={inheritsUiDirection ? dir : undefined}
              className={`${inputClassName} ${
                inputType === "date"
                  ? "hide-native-calendar date-input-placeholder"
                  : ""
              }`}
              disabled={isDisabled}
              aria-invalid={errors ? "true" : "false"}
              maxLength={maxLength}
              minLength={minLength}
              min={min}
              style={{
                paddingInlineStart: inputType === "date" ? "2.5rem" : undefined,
                ...uiDirectionStyle,
              }}
            />
          </>
        )}
        {inputType === "password" && !prefix && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute inset-e-3 top-[50%] translate-y-[-50%] text-muted-foreground hover:text-primary-text z-10"
          >
            {showPassword ? (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      {showCharCount && maxLength && (
        <p className="text-xs text-muted-foreground">
          {minLength
            ? t("common.characterCountWithMin", {
                count: String(currentLength),
                min: String(minLength),
                max: String(maxLength),
              })
            : t("website.richText.characterCount", {
                count: String(currentLength),
                max: String(maxLength),
              })}
        </p>
      )}
      {hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {errors && (
        <span className="text-sm text-danger" role="alert">
          {errors.message}
        </span>
      )}
    </div>
  );
};

export default InputForm;
